import { Button, Icon } from '@televet/kibble-ui';
import { Box, Flex, Grid, HStack, VStack, Wrap } from '@televet/kibble-ui/build/chakra';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { ChannelTagType } from '@televet/shared-types/JsonTypes/ChannelTag';
import { ModuleName } from '@televet/shared-types/productPackages';
import { add, format } from 'date-fns';
import { updateAppointmentModal } from 'pages/Appointments/state/appointmentsModalSlice';
import React, { useCallback } from 'react';
import HasModule from 'shared/components/HasModule';
import { getTagVariantByType } from 'shared/utils/tags';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { AppointmentInfo } from './AppointmentInfo';

const getFormattedDateValue = (startAt: Date | string | undefined, durationInMinutes: number | undefined): string => {
  if (!startAt) return 'N/A';

  const startDate = new Date(startAt);
  const endDate = add(startDate, { minutes: durationInMinutes });

  const dateFormatted = format(startDate, 'EEE, MMM d');
  const startTimeFormatted = format(startDate, 'h:mm a');
  const endTimeFormatted = format(endDate, 'h:mm a');

  return `${dateFormatted}\n${startTimeFormatted} - ${endTimeFormatted}`;
};

export const DetailsTab = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const { appointmentDraft } = useAppSelector((state) => state.appointmentsModal);

  const dateValue = React.useMemo(
    () => getFormattedDateValue(appointmentDraft.startAt, appointmentDraft.durationInMinutes),
    [appointmentDraft.startAt, appointmentDraft.durationInMinutes],
  );

  const providerName = React.useMemo(
    () =>
      appointmentDraft.clinicEmployee
        ? `${appointmentDraft.clinicEmployee.firstName} ${appointmentDraft.clinicEmployee.lastName}`
        : 'N/A',
    [appointmentDraft.clinicEmployee],
  );

  const onEditClick = useCallback(() => {
    dispatch(
      updateAppointmentModal({
        mode: 'edit',
      }),
    );
  }, [dispatch]);

  return (
    <VStack w="100%" spacing={4}>
      <Flex w="100%" justifyContent="space-between" alignItems="flex-start" mt={4}>
        <Grid templateColumns="1fr 1fr" gap={4} w="100%">
          <AppointmentInfo iconName="clock" label="DATE & TIME" value={dateValue} />
          {/* Rest of the component remains the same */}
          <HasModule moduleToCheck={ModuleName.AppointmentsStandard}>
            <AppointmentInfo
              iconName="calendarCheck"
              label="APPOINTMENT TYPE"
              value={appointmentDraft.appointmentType?.name || 'N/A'}
            />
            <AppointmentInfo iconName="person" label="PROVIDER" value={providerName} />
            <AppointmentInfo iconName="locationMarker" label="ROOM" value={appointmentDraft.room?.name || 'N/A'} />
          </HasModule>
        </Grid>
        <Button
          variant="secondarySubtle"
          size="sm"
          iconName="pen"
          aria-label="Edit appointment"
          onClick={onEditClick}
        />
      </Flex>
      <HasModule moduleToCheck={ModuleName.AppointmentsStandard}>
        <HStack w="100%" spacing={2} align="flex-start" backgroundColor="background.subtle" p={3} borderRadius="md">
          <Icon name="menu" size="lg" variant="subtle" />
          <Text as="p" size="sm" variant="subtle" maxW="calc(100% - 32px)">
            {appointmentDraft.description || 'Notes'}
          </Text>
        </HStack>
        <HStack spacing={2} w="100%">
          <Icon name="tag" size="md" variant="subtle" />
          <Wrap spacing={2} w="100%">
            {appointmentDraft.tags?.map((tag) => {
              const color =
                tag.tagType === ChannelTagType.Custom && tag.colorBackground
                  ? tag.colorBackground
                  : getTagVariantByType(tag.tagType);
              return (
                <Box key={tag.name} bg={`background.${color}`} px={2} py={1} borderRadius="md" fontSize="sm">
                  <Text as="span" fontWeight="semibold" color={`text.${color}`} lineHeight={1}>
                    {tag.name}
                  </Text>
                </Box>
              );
            })}
          </Wrap>
        </HStack>
      </HasModule>
    </VStack>
  );
};
